import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, Department} from 'yougile-model'
import {Button, LinkButton} from '@components/common/base/buttons'
import {DropdownList, MenuItem} from '@components/common/base/dropdown-list'
import {Input} from '@components/common/base/input'
import {Popup} from '@components/common/base/popup'
import {UsersTable} from '@components/common/base/users-table'
import {
  DepartmentEditorStore,
  Role
} from '@components/departments-edit/department-editor-store'
import {createUseStore, useGlobalKeyDown, useMemoOne} from '@containers/hooks'
export interface DepartmentEditorProps {
  allDepartments: Department[]
  userId: DataObjectId
  isAdmin: boolean
  canEdit: boolean
  department: Department | undefined
  allUsers: DataObjectId[]
  save: (d: Department) => void
  close: () => void
  remove: (d: Department) => void
}

const [StoreContext, useStore] = createUseStore(DepartmentEditorStore)

const UserRole = observer((props: {userId: DataObjectId}) => {
  const [state, store] = useStore()

  const isSelected = state.selected.includes(props.userId)

  if (!isSelected) {
    return null
  }

  const options: MenuItem<Role>[] = [
    {
      value: 'manager',
      label: Lang.msg.component.departmentEditor.departmentManager,
      iconChecked: true
    },
    {
      value: 'member',
      label: Lang.msg.component.departmentEditor.departmentUser,
      iconChecked: true
    }
  ]

  const roleValue = state.roles.get(props.userId)

  return (
    <DropdownList
      options={options}
      onChange={x => {
        store.setRole(props.userId, x)
      }}
      size="big"
      link
      value={roleValue}
      menuListClassName="w-200"
    />
  )
})

const TwForm = tw.div`
  flex
  mb-20
  gap-24
  justify-between
  px-4
`

const Form = observer((props: DepartmentEditorProps) => {
  const [state, store] = useStore()

  return (
    <TwForm>
      <div className="w-0 flex-1 shrink-0">
        <Input
          hasError={!!state.errors.departmentTitle}
          label={Lang.msg.permission.headerDep}
          value={state.departmentTitle}
          onChange={x => store.setTitle(x.text)}
          autoFocus={!state.departmentTitle}
          placeholder={Lang.msg.companies.departmentPlaceholder}
        />
      </div>
      <div className="w-0 flex-1 shrink-0">
        <DropdownList
          options={state.parentDepartments}
          onChange={x => {
            store.setParent(x, props)
          }}
          size="big"
          value={state.parentId}
          label={Lang.msg.component.departmentEditor.parentDepartment}
        />
      </div>
    </TwForm>
  )
})

const DepartmentEditor = observer((props: DepartmentEditorProps) => {
  const [, store] = useStore()
  return (
    <>
      <Form {...props} />
      <UsersTable
        className="flex-1 overflow-hidden pt-4 px-4"
        disableClickableRows
        useCheckbox
        users={store.state.users}
        selected={store.state.selected}
        onSelectedChanged={x => store.setSelected(x)}
        showPositionColumn
        lastColumn={{
          title: Lang.msg.component.departmentEditor.departmentRole,
          width: 200,
          render: u => <UserRole userId={u.userId} />
        }}
        large
      />
    </>
  )
})

interface DepartmentEditorWindowProps {
  allDepartments: Department[]
  userId: DataObjectId
  isAdmin: boolean
  canEdit: boolean
  department: Department | undefined
  allUsers: DataObjectId[]
  save: (d: Department) => void
  close: () => void
  remove: (d: Department) => void
}

export const DepartmentEditorWindow = (props: DepartmentEditorWindowProps) => {
  const {canEdit, department, save, remove, close} = props
  const store = useMemoOne(() => new DepartmentEditorStore(props), [])
  const [title, buttonTitle] = department
    ? [
        Lang.msg.component.departmentEditor.editDepartment,
        Lang.msg.component.departmentEditor.deleteDepartment
      ]
    : [
        Lang.msg.component.departmentEditor.newDepartment,
        Lang.msg.component.departmentEditor.createDepartment
      ]

  useGlobalKeyDown({
    escape: () => {
      close()
      return true
    }
  })

  const saveDepartment = () => {
    const updatedDepartment = store.updateDepartment(department)
    if (updatedDepartment) {
      save(updatedDepartment)
    }
  }

  const footerLinkClick = () => {
    if (department) {
      remove(department)
    } else {
      saveDepartment()
    }
  }

  const footer = canEdit ? (
    <>
      {department ? (
        <div className="flex gap-16 justify-end">
          <LinkButton color="error" icon="IconTrash" onClick={footerLinkClick}>
            {buttonTitle}
          </LinkButton>
          <Button onClick={saveDepartment}>{Lang.msg.saveChanges}</Button>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button onClick={footerLinkClick}>{buttonTitle}</Button>
        </div>
      )}
    </>
  ) : null

  return (
    <Popup
      close={close}
      title={title}
      width={924}
      footer={footer}
      contentClassName={`flex flex-col overflow-hidden ${
        canEdit ? 'pb-16' : 'pb-32'
      } -mx-4 pt-16`}
      noScroll
    >
      <StoreContext.Provider value={store}>
        <DepartmentEditor {...props} />
      </StoreContext.Provider>
    </Popup>
  )
}
